import Auth from '@Services/Auth';
import { paths } from '@AppRoutes/constants';
import { StructureFormats } from '@Pages/Hopinto/constants';
import { goToLanding, showInvalidParamsDialog } from '@Pages/Hopinto/actions';
import { createProjectFromReaction, SOURCE_LABEL } from '@Pages/Conditions/ConditionsService';
import { resetConditionsNavigator } from '@Pages/Conditions/navigation';

const INVALID_REACTION_STATUS = 422;
const INVALID_REACTION_CODE = 'INVALID_REACTION';

const isInvalidReactionError = (error) =>
  error?.code === INVALID_REACTION_CODE || error?.status === INVALID_REACTION_STATUS || error?.statusCode === INVALID_REACTION_STATUS;

/**
 * Processes a hopinto link with context=CN (Condition Navigator). Mirrors the RT hopinto flow:
 * the reaction is parsed from the link params on the FE and sent to the regular CN predict API
 * (POST /api/conditionNavigator/predict), which creates a new project in one call. The `options`
 * link parameter is not supported for CN and is ignored; `qname` becomes the project name.
 *
 * Routing per ticket:
 * - query is not a reaction SMILES -> landing page with the invalid-params dialog;
 * - reaction rejected by validation (HTTP 422 INVALID_REACTION, no project created) -> CN
 *   projects page with the warning message;
 * - project created with predicted conditions -> CN results page for that project
 *   (validation warnings, if any, are intentionally ignored);
 * - project created without conditions -> CN projects page with the "no conditions found" notice.
 *
 * @param {Object} params hopinto URL params (query, qname, ...)
 * @param {Object} history react-router history
 */
// eslint-disable-next-line import/prefer-default-export
export const createCNProject = (params, history) => {
  const { query, qname } = params;

  // unlike RT, CN supports reaction SMILES queries only (query=SMILES='reactants>>product')
  if (!query || !query.includes(`${StructureFormats.SMILES}=`)) {
    goToLanding();
    return showInvalidParamsDialog();
  }
  const reaction = query.replace(`${StructureFormats.SMILES}=`, '').replace(/['"]+/g, '');
  if (!reaction.includes('>>')) {
    goToLanding();
    return showInvalidParamsDialog();
  }
  return (async () => {
    try {
      const project = await createProjectFromReaction({
        reaction,
        name: qname,
        referenceType: 'hopinto',
        reference: { type: 'hopinto' },
        // keep the "External link" source pill even when qname is set as the project name
        referenceLabel: SOURCE_LABEL.hopinto,
        // handle the invalid-reaction rejection here instead of the generic error modal
        validErrorStatusCodes: [INVALID_REACTION_STATUS],
      });
      resetConditionsNavigator();

      if (project?.id && (project?.resultCount > 0 || project?.uniqueReactions > 0)) {
        history.push(`${paths.CONDITIONS}/${project.id}`);
        return;
      }
      history.push({ pathname: paths.CONDITIONS, state: { noConditionsFound: true } });
    } catch (error) {
      Logger.debug(error);
      if (isInvalidReactionError(error)) {
        // reaction rejected by the validation service - no project was created
        resetConditionsNavigator();
        history.push({
          pathname: paths.CONDITIONS,
          state: { conditionWarningMessages: [error?.message || 'The reaction is not supported.'] },
        });
        return;
      }
      // other server errors (license, rate limit, ...) are notified by the generic error flow
      if (Auth.getAccessToken()) {
        goToLanding();
      }
    }
  })();
};
